/**
 * OPERATOR RAMP-UP: 2-Week Re-Entry Program
 *
 * Purpose: Return to operator-level training after 2+ weeks off
 * (illness, travel, renovation, injury recovery, etc.)
 *
 * This is NOT "Chassis Cut at lower volume." This is a distinct
 * re-entry protocol designed to rebuild from the ground up.
 *
 * WEEK 1 — RE-ENTRY (4 days)
 *   Full-body sessions focused on movement quality, connective tissue
 *   readiness, tempo work, and aerobic base rebuilding. Every session
 *   includes Z2 and mobility. No grinding reps. RPE 5-6.
 *
 * WEEK 2 — RAMP (5-6 days)
 *   Transition toward the Chassis Cut split. Re-introduce strength
 *   signals, sled work, and carries. Progressive loading. End-of-week
 *   readiness check. RPE 7-8.
 *
 * ALL exercises sourced from internal exerciseDatabase / freeDB.
 */

import type {
  WorkoutProgram,
  WorkoutTemplateDay,
  ProgramMetadata,
  ProgramPeriodizationModel,
} from '../types/program';

// ============================================================================
// METADATA
// ============================================================================

const metadata: ProgramMetadata = {
  id: 'operator-ramp-up-2week',
  name: 'OPERATOR RAMP-UP: 2-Week Re-Entry',
  description:
    'Return-to-training protocol after extended time off. Week 1 rebuilds movement patterns and aerobic base with full-body sessions. Week 2 ramps toward operator-level loading and reintroduces the Chassis Cut split. Ends with a readiness check.',
  level: 'operator',
  experienceLevel: 'advanced',
  duration: 2,
  daysPerWeek: 5,
  goals: ['strength', 'conditioning', 'work-capacity', 'tactical-performance'],
  difficulty: 'intermediate',
  tags: ['ramp-up', 're-entry', 'bridge', 'return-to-training'],
  emphasis: 'Movement quality → progressive loading → readiness confirmation',
  version: '2.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  usesPeriodization: true,
  hasRangeDay: false,
  requiredEquipment: 'full-gym',
  // Each training day finishes with a 20-25 min Z2 walk → 3-4 sessions
  // × 20 min ≈ 60 min/wk. Reentry-appropriate target, not chasing
  // adaptation yet — just rebuilding aerobic baseline.
  weeklyZ2Target: 60,
};

// ============================================================================
// PERIODIZATION
// ============================================================================

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'accumulation',
      name: 'Re-Entry — Pattern & Base',
      focus: 'Full-body movement quality. Tempo work, pauses, controlled eccentrics. Z2 every session. RPE 5-6 (RIR 4+). No grinding.',
      intensityMultiplier: 0.65,
      volumeMultiplier: 0.6,
      trainingParameters: {
        repRange: [10, 15],
        setsMultiplier: 0.6,
        restPeriod: '90-120s',
      },
    },
    {
      weeks: [2],
      type: 'intensification',
      name: 'Ramp — Load & Split',
      focus: 'Transition to Chassis Cut split. Re-introduce strength signals, sled, carries. RPE 7-8 (RIR 2-3). End-of-week readiness check.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.85,
      progression: {
        type: 'linear',
        startIntensity: 0.72,
        endIntensity: 0.82,
        weeklyIncrease: 0.10,
      },
      trainingParameters: {
        repRange: [6, 12],
        setsMultiplier: 0.85,
        restPeriod: '90-120s',
      },
    },
  ],
};

// ============================================================================
// WEEK 1: RE-ENTRY — Full Body (4 training days + 3 rest)
// Focus: movement quality, connective tissue, aerobic base, mobility
// ============================================================================

// --- W1 DAY 1: FULL BODY A — Squat Pattern + Push + Pull + Z2 ---
const w1Day1: WorkoutTemplateDay = {
  name: 'Full Body A — Squat + Push/Pull + Z2',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Movement Prep + Mobility',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike or incline walk — longer warm-up after time off' },
        { name: 'Cat-Cow', sets: 1, reps: 10, format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '45s/side', format: 'prep' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 12, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
      ],
    },
    {
      name: 'Squat Pattern — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'light-moderate', rest: '90s', details: '3-sec eccentric, 1-sec pause at bottom. Feel the pattern, don\'t chase weight.' },
      ],
    },
    {
      name: 'Push/Pull — Controlled',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Push-Ups', sets: 3, reps: '10-15', intensity: 'bodyweight', rest: '0:00', details: '2-sec eccentric. Scale to incline if needed after time off.' },
        { name: 'Dumbbell Row', sets: 3, reps: '10-12/side', intensity: 'light', rest: '90s', details: '1-sec pause at top. Rebuild scapular control.' },
      ],
    },
    {
      name: 'Accessory — Posterior + Core',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Romanian Deadlift', sets: 2, reps: 12, intensity: 'light', rest: '0:00', details: 'Dumbbells. Slow eccentric — feel the hamstrings wake up.' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s', details: 'Rebuild core bracing before heavier loads come back.' },
      ],
    },
    {
      name: 'Zone 2 — Base Rebuild',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '20 min', details: 'Z2 HR. Conversational pace. Rebuild aerobic base — this is non-negotiable every session W1.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 2: REST ---
const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

// --- W1 DAY 3: FULL BODY B — Hinge Pattern + Overhead + Carry + Z2 ---
const w1Day3: WorkoutTemplateDay = {
  name: 'Full Body B — Hinge + Overhead + Carry + Z2',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Rower — easy pace' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep' },
        { name: 'Frog Stretch', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '20s + 8 reps', format: 'prep' },
        { name: 'Shoulder Y-T-W', sets: 1, reps: '6 each', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: 15, format: 'prep' },
      ],
    },
    {
      name: 'Hinge Pattern — Tempo',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 3, reps: 10, intensity: 'light-moderate', rest: '90s', details: '3-sec eccentric. Barbell or dumbbells. Re-establish hip hinge mechanics.' },
      ],
    },
    {
      name: 'Overhead + Row',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Dumbbell Shoulder Press', sets: 3, reps: 10, intensity: 'light', rest: '0:00', details: 'Seated. Controlled press — let shoulders remember the pattern.' },
        { name: 'Seated Cable Row', sets: 3, reps: 12, intensity: 'light', rest: '90s', details: '1-sec squeeze. Posture emphasis.' },
      ],
    },
    {
      name: 'Carry — Re-introduction',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 3, reps: '30m', intensity: 'moderate', rest: '60s', details: 'Light-moderate load. Focus on posture and grip endurance. Not a test — just wake up the pattern.' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s', details: 'Anti-rotation bracing.' },
        { name: 'Side Plank', sets: 2, reps: '20s/side', rest: '30s' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '20 min', details: 'Z2. Steady.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Pigeon Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 5: FULL BODY C — Pull Focus + Single Leg + Z2 ---
const w1Day5: WorkoutTemplateDay = {
  name: 'Full Body C — Pull Focus + Single Leg + Z2',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike' },
        { name: 'Dead Hang', duration: '20-30s', format: 'prep' },
        { name: 'Straight-Arm Pulldown', sets: 2, reps: 12, format: 'prep' },
        { name: 'T-Spine Extension', sets: 1, reps: 8, format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '25s', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 1, reps: '10 steps each way', format: 'prep' },
      ],
    },
    {
      name: 'Pull — Volume',
      type: 'strength',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: 12, intensity: 'light-moderate', rest: '90s', details: 'Full ROM, slow eccentric. Rebuild lat engagement before pull-ups come back.' },
        { name: 'Chest Supported Row', sets: 3, reps: 10, intensity: 'light-moderate', rest: '90s', details: '1-sec pause. Scapular retraction emphasis.' },
      ],
    },
    {
      name: 'Single Leg — Stability',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bulgarian Split Squat', sets: 2, reps: '8/side', intensity: 'bodyweight or light DBs', rest: '0:00', details: 'Controlled descent. Balance and stability focus — connective tissue readiness.' },
        { name: 'Single-Leg Romanian Deadlift', sets: 2, reps: '8/side', intensity: 'light', rest: '60s', details: 'Use DB for counterbalance. Rebuild proprioception.' },
      ],
    },
    {
      name: 'Upper Accessory',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Face Pull', sets: 2, reps: 15, rest: '0:00' },
        { name: 'Dumbbell Curl', sets: 2, reps: 12, intensity: 'light', rest: '60s', details: 'Tendons need re-loading. Don\'t skip the easy sets.' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '20-25 min', details: 'Z2. Push duration slightly from Day 1.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery' },
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W1 DAY 7: FULL BODY D — Push Focus + Posterior + Longer Z2 ---
const w1Day7: WorkoutTemplateDay = {
  name: 'Full Body D — Push Focus + Posterior + Long Z2',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 2, reps: 10, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 12, format: 'prep' },
        { name: 'Soleus Raise', sets: 2, reps: 15, format: 'prep' },
        { name: 'Step-Downs', sets: 2, reps: '6/side', format: 'prep' },
      ],
    },
    {
      name: 'Push — Tempo',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: 10, intensity: 'light-moderate', rest: '0:00', details: '3-sec eccentric. No ego weight — feel the chest and shoulders working.' },
        { name: 'Dumbbell Row', sets: 3, reps: '10/side', intensity: 'light-moderate', rest: '90s' },
      ],
    },
    {
      name: 'Posterior Chain',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Hip Thrust', sets: 3, reps: 12, intensity: 'moderate', rest: '0:00', details: 'Rebuild glute activation before heavy hinge loading in W2.' },
        { name: 'Back Extension', sets: 2, reps: 12, intensity: 'bodyweight', rest: '60s' },
      ],
    },
    {
      name: 'Core + Stability',
      type: 'accessory',
      exercises: [
        { name: 'McGill Curl-Up', sets: 2, reps: 8, rest: '0:00', details: '6-sec holds' },
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Zone 2 — Extended',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '25-30 min', details: 'Longest Z2 of the week. If it feels easy, you\'re ready for W2.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// WEEK 2: RAMP — Transition to Split (6 days)
// Re-introduce strength signals, sled, carries, higher intensity
// ============================================================================

// --- W2 DAY 1: LOWER — Squat Signal + Sled + Carry ---
const w2Day1: WorkoutTemplateDay = {
  name: 'Lower — Squat Signal + Sled Re-intro',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band Lateral Walk', sets: 1, reps: '10 steps each way', format: 'prep' },
        { name: 'Step-Downs', sets: 2, reps: '6/side', format: 'prep', details: '3-sec eccentric' },
      ],
    },
    {
      name: 'Strength Signal — Squat',
      type: 'strength',
      exercises: [
        { name: 'Leg Press', sets: 4, reps: '8-10', intensity: 'RPE 7', rest: '2 min', details: 'First real loading. Build across sets — last set should feel like work but not a grind.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Leg Extension', sets: 3, reps: '12-15', intensity: 'progressive', rest: '0:00' },
        { name: 'Seated Ham Curl', sets: 3, reps: '10-12', intensity: 'progressive', rest: '60-90s' },
      ],
    },
    {
      name: 'Sled Re-introduction',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 5, reps: '15m', intensity: 'light-moderate', rest: '60s', details: 'First sled work in 2+ weeks. Start conservative. Build confidence, not a PR.' },
        { name: 'Farmer Carry', sets: 3, reps: '40m', intensity: 'moderate', rest: '60s' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 2: UPPER — Pull Signal + Push + HIIT Re-intro ---
const w2Day2: WorkoutTemplateDay = {
  name: 'Upper — Pull Signal + Push + HIIT',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Rower' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 12, format: 'prep' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep' },
        { name: 'Face Pull', sets: 2, reps: 15, format: 'prep' },
        { name: 'T-Spine Extension', sets: 1, reps: 8, format: 'prep' },
        { name: 'Dead Hang', duration: '20s', format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Pull',
      type: 'strength',
      exercises: [
        { name: 'Weighted Pull-Up', sets: 4, reps: '3-5', intensity: 'RPE 7', rest: '2-3 min', details: 'First heavy pull in 2+ weeks. Start with bodyweight, add weight only if clean.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 3, reps: '8-10', intensity: 'progressive', rest: '0:00' },
        { name: 'Chest Supported Row', sets: 3, reps: '8-10', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Cable Lateral Raise', sets: 3, reps: '12-15', rest: '0:00' },
        { name: 'Reverse Pec Deck', sets: 3, reps: '12-15', rest: '60s' },
      ],
    },
    {
      name: 'Arms',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Hammer Curl', sets: 2, reps: '10-12', rest: '0:00' },
        { name: 'Overhead Tricep Extension', sets: 2, reps: '10-12', rest: '60s' },
      ],
    },
    {
      name: 'HIIT Re-intro',
      type: 'conditioning',
      exercises: [
        { name: 'Row/Bike Sprint', sets: 5, reps: 1, format: 'intervals', details: '15s hard / 60s easy x5. Reintroduce intensity — shorter than Chassis Cut HIIT.', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 3: Z2 + CARRIES + CHASSIS ---
const w2Day3: WorkoutTemplateDay = {
  name: 'Z2 + Carries + Chassis Core',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep' },
        { name: 'TKE', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '15/side', format: 'prep' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '30-35 min', details: 'Building toward Chassis Cut\'s 35-50 min Z2. Conversational pace.' },
      ],
    },
    {
      name: 'Carry Density',
      type: 'conditioning',
      exercises: [
        { name: 'Farmer Carry', sets: 5, reps: '40m', intensity: 'moderate-heavy', rest: '45s', details: 'Heavier than W1. Testing grip and trunk endurance.' },
        { name: 'Suitcase Carry', sets: 3, reps: '30m/side', intensity: 'moderate', rest: '45s' },
      ],
    },
    {
      name: 'Chassis Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'Pallof Press', sets: 2, reps: '12/side', rest: '0:00' },
        { name: 'Side Plank', sets: 2, reps: '30s/side', rest: '0:00' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '60s' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Soleus Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 4: LOWER — Hinge Signal + Backward Drag ---
const w2Day4: WorkoutTemplateDay = {
  name: 'Lower — Hinge Signal + Backward Drag',
  sessionType: 'pt',
  estimatedDuration: 65,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep' },
        { name: 'Seated Ham Curl', sets: 1, reps: 15, format: 'prep', details: 'Light warm-up' },
        { name: 'Glute Bridge Iso', sets: 1, reps: '20s + 8 reps', format: 'prep' },
        { name: 'Frog Stretch', sets: 1, reps: '8/side', format: 'prep' },
        { name: 'Split-Stance Hinge', sets: 1, reps: '8/side', format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Hinge',
      type: 'strength',
      exercises: [
        { name: 'Romanian Deadlift', sets: 4, reps: '6-8', intensity: 'RPE 7-8', rest: '2-3 min', details: 'Barbell. This should feel like real work. If form breaks, drop weight.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Seated Ham Curl', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Tibialis Raise', sets: 3, reps: '15-20', rest: '60-90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Hip Thrust', sets: 3, reps: '10-12', intensity: 'progressive', rest: '0:00' },
        { name: 'Cable Crunch', sets: 3, reps: '10-15', rest: '60-90s' },
      ],
    },
    {
      name: 'Backward Drag',
      type: 'conditioning',
      exercises: [
        { name: 'Backward Drag', sets: 8, reps: '15m', intensity: 'moderate', rest: '40s', details: 'First backward drag session. Light-moderate. Knees should feel good after these.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Glute Stretch', duration: '60s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 5: UPPER — Push Signal + Pull Volume + Z2 ---
const w2Day5: WorkoutTemplateDay = {
  name: 'Upper — Push Signal + Pull Volume + Z2',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Rower' },
        { name: 'Dead Hang', duration: '20s', format: 'prep' },
        { name: 'Straight-Arm Pulldown', sets: 2, reps: 12, format: 'prep' },
        { name: 'Shoulder Y-T-W', sets: 1, reps: '6 each', format: 'prep' },
        { name: 'Serratus Wall Slide', sets: 1, reps: 10, format: 'prep' },
      ],
    },
    {
      name: 'Strength Signal — Press',
      type: 'strength',
      exercises: [
        { name: 'Incline Dumbbell Press', sets: 4, reps: '6-8', intensity: 'RPE 7-8', rest: '2-3 min', details: 'Heaviest pressing of the program. If this feels solid, you\'re ready for Chassis Cut.' },
      ],
    },
    {
      name: 'Superset A',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Lat Pulldown', sets: 3, reps: '8-10', intensity: 'progressive', rest: '0:00' },
        { name: 'Landmine Press', sets: 3, reps: '8-10/side', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      name: 'Superset B',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: '1-Arm Cable Row', sets: 3, reps: '10/side', intensity: 'progressive', rest: '0:00' },
        { name: 'Machine Chest Press', sets: 2, reps: '10-12', intensity: 'progressive', rest: '60s' },
      ],
    },
    {
      name: 'Arms',
      type: 'accessory',
      format: 'superset',
      exercises: [
        { name: 'Barbell Curl', sets: 2, reps: '8-12', rest: '0:00' },
        { name: 'Triceps Pressdown', sets: 2, reps: '10-15', rest: '60s' },
      ],
    },
    {
      name: 'Carry',
      type: 'conditioning',
      exercises: [
        { name: 'Bottoms-Up KB Carry', sets: 3, reps: '15m/side', intensity: 'moderate', rest: '30s' },
      ],
    },
    {
      name: 'Zone 2',
      type: 'conditioning',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '15-20 min', details: 'Mandatory Z2. Building back endurance floor.' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Lat Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// --- W2 DAY 6: READINESS CHECK — Test Key Movements + Metcon ---
const w2Day6: WorkoutTemplateDay = {
  name: 'Readiness Check — Are You Chassis Cut Ready?',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep' },
        { name: 'Hip Airplanes', sets: 1, reps: '5/side', format: 'prep' },
        { name: 'Scapular Push-Ups', sets: 1, reps: 10, format: 'prep' },
        { name: 'TKE', sets: 2, reps: '15/side', format: 'prep' },
        { name: 'Band External Rotation', sets: 1, reps: '12/side', format: 'prep' },
      ],
    },
    {
      name: 'Readiness Test — Strength',
      type: 'strength',
      exercises: [
        { name: 'Pull-Ups', sets: 1, reps: 'Max', intensity: 'bodyweight', rest: '3 min', details: 'Max strict pull-ups. Compare to your baseline. >80% of previous max = green light.' },
        { name: 'Leg Press', sets: 1, reps: '8-10', intensity: 'RPE 8', rest: '2 min', details: 'One working set at your target W1 Chassis Cut weight. Does it move clean?' },
      ],
    },
    {
      name: 'Readiness Test — Conditioning',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Sled Push', reps: '20m', format: 'circuit', details: '4 rounds. Chassis Cut pace — can you sustain it?' },
        { name: 'Farmer Carry', reps: '40m', format: 'circuit' },
        { name: 'Pull-Ups', reps: '6-8', format: 'circuit' },
        { name: 'Landmine Press', reps: '8/side', format: 'circuit' },
      ],
    },
    {
      name: 'Armor Core',
      type: 'accessory',
      format: 'circuit',
      exercises: [
        { name: 'McGill Curl-Up', sets: 2, reps: 8, rest: '0:00', details: '8-sec holds' },
        { name: 'Dead Bug', sets: 2, reps: '8/side', rest: '0:00' },
        { name: 'Pallof Press', sets: 2, reps: '10/side', rest: '60s' },
      ],
    },
    {
      name: 'Debrief',
      type: 'recovery',
      exercises: [
        { name: 'Treadmill Incline Walk', duration: '10 min', details: 'Cool-down walk. Assess: joints feel good? Energy stable? If yes — start Chassis Cut tomorrow.' },
        { name: 'Hip Flexor Stretch', duration: '60s/side', format: 'recovery' },
        { name: 'Open Books', sets: 1, reps: '6/side', format: 'recovery' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery' },
      ],
    },
  ],
};

// ============================================================================
// PROGRAM EXPORT
// ============================================================================

export const operatorRampUp2Week: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    // Week 1: Full body, 4 training days (Day 1, 3, 5, 7 — rest on 2, 4, 6)
    'week1-day1': w1Day1,
    'week1-day2': restDay,
    'week1-day3': w1Day3,
    'week1-day4': restDay,
    'week1-day5': w1Day5,
    'week1-day6': restDay,
    'week1-day7': w1Day7,

    // Week 2: Split transition, 6 days (rest on Day 6 → Day 7 is rest)
    'week2-day1': w2Day1,
    'week2-day2': w2Day2,
    'week2-day3': w2Day3,
    'week2-day4': w2Day4,
    'week2-day5': w2Day5,
    'week2-day6': w2Day6,
    'week2-day7': restDay,
  },
};
